import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  Crown,
  Loader2,
  LogOut,
  Brain,
  Trash2,
  UserMinus,
} from "lucide-react";
import UsagePanel from "@/components/UsagePanel";
import BillingPanel from "@/components/BillingPanel";
import SectionErrorBoundary from "@/components/SectionErrorBoundary";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspaces } from "@/contexts/WorkspaceContext";
import { toast } from "sonner";
import InvitesPanel from "@/components/InvitesPanel";

const ROLE_LABEL = { owner: "Owner", admin: "Admin", member: "Member" };

function avatarBg(name) {
  if (name === "Arthur") return "#D96C4A";
  if (name === "Simon") return "#3A5A40";
  const palette = ["#5B4C7A", "#2C5F77", "#7A4A2C", "#4B6C3A", "#7A3D5D"];
  let h = 0;
  for (const c of name || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
}

function NotificationPrefsSection({ user }) {
  const defaults = {
    mentions: true,
    trial_alerts: true,
    subscription: true,
    billing: true,
  };
  const initial = { ...defaults, ...(user?.notification_prefs || {}) };
  const [prefs, setPrefs] = useState(initial);
  const [saving, setSaving] = useState(null);

  const toggle = async (key) => {
    const next = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: next }));
    setSaving(key);
    try {
      await api.patch("/auth/me/preferences", { [key]: next });
    } catch (e) {
      setPrefs((p) => ({ ...p, [key]: !next }));
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  const rows = [
    {
      key: "mentions",
      title: "@mention emails",
      body: "Get emailed when a teammate @mentions you in a chat. Throttled to one email per chat per 5 minutes.",
    },
    {
      key: "trial_alerts",
      title: "Trial budget alerts",
      body: "Heads-up at 50%, 80%, and 100% of your free trial credit.",
    },
    {
      key: "subscription",
      title: "Subscription updates",
      body: "Welcome, plan changes, and cancellation confirmations.",
    },
    {
      key: "billing",
      title: "Billing receipts",
      body: "Top-up confirmations and payment-failure suspension notices.",
    },
  ];

  return (
    <section
      className="space-y-3 mt-6"
      data-testid="settings-section-notifications"
    >
      <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
        Your notifications
      </h3>
      <div className="rounded-xl border border-[#DCD7CE] bg-white divide-y divide-[#ECE7DC]">
        {rows.map((r) => (
          <div
            key={r.key}
            className="flex items-start justify-between gap-4 px-4 py-3.5"
            data-testid={`notif-pref-row-${r.key}`}
          >
            <div className="flex-1 min-w-0">
              <p className="font-sans text-sm font-medium text-[#2A2D34]">
                {r.title}
              </p>
              <p className="font-sans text-xs text-[#7A7571] mt-1 leading-relaxed">
                {r.body}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={prefs[r.key]}
              onClick={() => toggle(r.key)}
              disabled={saving === r.key}
              className={`mt-0.5 shrink-0 relative inline-flex h-6 w-11 cursor-pointer items-center rounded-full border-0 p-0 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#1E7BFF]/30 disabled:opacity-60 disabled:cursor-not-allowed ${
                prefs[r.key] ? "bg-[#1E7BFF]" : "bg-[#D6D2C9]"
              }`}
              data-testid={`notif-pref-toggle-${r.key}`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                  prefs[r.key] ? "translate-x-[22px]" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function SettingsModal({ workspace, open, onOpenChange }) {
  const { user } = useAuth();
  const { refetch, setWorkspaces, workspaces } = useWorkspaces();
  const navigate = useNavigate();

  const [name, setName] = useState(workspace?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);

  // Phase 7b — project memory
  const memoryOn = !!workspace?.project_memory_enabled;
  const [memoryEstimate, setMemoryEstimate] = useState(null);
  const [memoryConfirmOpen, setMemoryConfirmOpen] = useState(false);
  const [memorySaving, setMemorySaving] = useState(false);

  const isOwner = workspace?.role === "owner";
  const isAdminOrOwner = isOwner || workspace?.role === "admin";

  useEffect(() => {
    setName(workspace?.name || "");
  }, [workspace?.name]);

  useEffect(() => {
    if (!open) {
      setDeleteOpen(false);
      setDeleteConfirm("");
      setLeaveOpen(false);
      setTransferOpen(false);
      setTransferTarget(null);
      setMemoryConfirmOpen(false);
      setMemoryEstimate(null);
    }
  }, [open]);

  const loadMembers = useCallback(async () => {
    if (!workspace?.id || !open) return;
    setLoadingMembers(true);
    try {
      const { data } = await api.get(`/workspaces/${workspace.id}/members`);
      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMembers(false);
    }
  }, [workspace?.id, open]);

  useEffect(() => {
    if (open) loadMembers();
  }, [open, loadMembers]);

  // Phase 6a: when InvitesPanel does an "add by email — existing user"
  // it fires this event so we refresh the members list immediately.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e?.detail?.workspace_id === workspace?.id) loadMembers();
    };
    window.addEventListener("onebrain:members-changed", handler);
    return () =>
      window.removeEventListener("onebrain:members-changed", handler);
  }, [open, workspace?.id, loadMembers]);

  if (!workspace) return null;

  const saveName = async () => {
    const next = name.trim();
    if (!next || next === workspace.name || savingName) return;
    setSavingName(true);
    try {
      const { data } = await api.patch(`/workspaces/${workspace.id}`, {
        name: next,
      });
      setWorkspaces((prev) =>
        prev.map((w) => (w.id === data.id ? { ...w, name: data.name } : w))
      );
      toast.success("Workspace renamed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingName(false);
    }
  };

  const changeRole = async (m, newRole) => {
    if (newRole === m.role) return;
    try {
      await api.patch(`/workspaces/${workspace.id}/members/${m.user_id}`, {
        role: newRole,
      });
      await loadMembers();
      toast.success(`${m.display_name} is now ${ROLE_LABEL[newRole]}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeMember = async (m) => {
    try {
      await api.delete(`/workspaces/${workspace.id}/members/${m.user_id}`);
      await loadMembers();
      toast.success(`${m.display_name} removed`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const transferOwnership = async (targetId) => {
    try {
      await api.post(`/workspaces/${workspace.id}/transfer-ownership`, {
        new_owner_user_id: targetId,
      });
      await refetch();
      await loadMembers();
      setTransferOpen(false);
      setTransferTarget(null);
      toast.success("Ownership transferred");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const leaveWorkspace = async () => {
    try {
      await api.delete(`/workspaces/${workspace.id}/members/${user.id}`);
      await refetch();
      onOpenChange(false);
      const remaining = workspaces.filter((w) => w.id !== workspace.id);
      if (remaining.length > 0) navigate(`/w/${remaining[0].slug}`);
      else navigate("/all-chats");
      toast.success(`You left ${workspace.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteWorkspace = async () => {
    try {
      await api.delete(`/workspaces/${workspace.id}?confirm=DELETE`);
      await refetch();
      onOpenChange(false);
      const remaining = workspaces.filter((w) => w.id !== workspace.id);
      if (remaining.length > 0) navigate(`/w/${remaining[0].slug}`);
      else navigate("/all-chats");
      toast.success(`${workspace.name} deleted`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Phase 7b — project memory
  const setProjectMemory = async (next) => {
    if (memorySaving) return;
    setMemorySaving(true);
    try {
      const { data } = await api.patch(`/workspaces/${workspace.id}`, {
        project_memory_enabled: next,
      });
      setWorkspaces((prev) =>
        prev.map((w) =>
          w.id === data.id
            ? {
                ...w,
                project_memory_enabled: data.project_memory_enabled,
                project_memory_updated_at: data.project_memory_updated_at,
              }
            : w
        )
      );
      toast.success(
        next ? "Project memory enabled" : "Project memory disabled"
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMemorySaving(false);
    }
  };

  const openMemoryConfirm = async () => {
    if (!isAdminOrOwner) return;
    setMemoryConfirmOpen(true);
    setMemoryEstimate(null);
    try {
      const { data } = await api.get(
        `/workspaces/${workspace.id}/project-memory-estimate`
      );
      setMemoryEstimate(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleMemoryToggle = () => {
    if (!isAdminOrOwner) return;
    if (memoryOn) {
      // Cheap to disable — flip immediately.
      setProjectMemory(false);
    } else {
      // Show cost preview before enabling.
      openMemoryConfirm();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
          data-testid="settings-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Workspace settings
            </DialogTitle>
            <DialogDescription className="font-sans text-sm text-[#7A7571]">
              Manage{" "}
              <span className="font-medium text-[#2C2A29]">
                {workspace.name}
              </span>
              . You're{" "}
              <span className="font-medium text-[#2C2A29]">
                {ROLE_LABEL[workspace.role || "member"]?.toLowerCase()}
              </span>
              .
            </DialogDescription>
          </DialogHeader>

          {/* General */}
          <section className="space-y-3" data-testid="settings-section-general">
            <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
              General
            </h3>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label
                  htmlFor="settings-workspace-name"
                  className="block font-sans text-xs text-[#7A7571] mb-1"
                >
                  Workspace name
                </label>
                <input
                  id="settings-workspace-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isAdminOrOwner}
                  data-testid="settings-workspace-name-input"
                  className="w-full px-4 py-2.5 rounded-lg bg-white border border-[#DCD7CE] font-sans text-base text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 disabled:bg-[#F3F1EC] disabled:text-[#7A7571] disabled:cursor-not-allowed"
                />
              </div>
              <Button
                type="button"
                onClick={saveName}
                disabled={
                  !isAdminOrOwner ||
                  savingName ||
                  name.trim() === workspace.name ||
                  !name.trim()
                }
                data-testid="settings-save-name-button"
                className="bg-[#2C2A29] hover:bg-[#1A1918] text-white"
              >
                {savingName ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
            <p className="font-sans text-xs text-[#A5A29E]">
              Slug:{" "}
              <code className="font-mono text-[11px] bg-[#F3F1EC] px-1.5 py-0.5 rounded">
                {workspace.slug}
              </code>{" "}
              · stays the same even if you rename
            </p>
          </section>

          {/* Phase 8 — Usage (owner/admin only) */}
          <SectionErrorBoundary
            label="Usage"
            testid="settings-section-usage-error"
          >
            <UsagePanel workspace={workspace} />
          </SectionErrorBoundary>

          {/* Phase 9 — Billing (owner/admin; mutations owner-only) */}
          <SectionErrorBoundary
            label="Billing"
            testid="settings-section-billing-error"
          >
            <BillingPanel workspace={workspace} />
          </SectionErrorBoundary>

          {/* Phase 10 — Per-user notification preferences */}
          <SectionErrorBoundary
            label="Notifications"
            testid="settings-section-notifications-error"
          >
            <NotificationPrefsSection user={user} />
          </SectionErrorBoundary>

          {/* Phase 7b — Project memory */}
          <section
            className="space-y-3 mt-6"
            data-testid="settings-section-project-memory"
          >
            <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold">
              Project memory
            </h3>
            <div
              className="flex items-start justify-between gap-4 px-4 py-3.5 rounded-xl border border-[#DCD7CE] bg-[#FBF8F2]"
              data-testid="project-memory-row"
              data-memory-on={memoryOn ? "true" : "false"}
            >
              <div className="flex gap-3 min-w-0 flex-1">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    memoryOn
                      ? "bg-[#D96C4A]/15 text-[#7A4A2C]"
                      : "bg-[#F3F1EC] text-[#7A7571]"
                  }`}
                  aria-hidden="true"
                >
                  <Brain size={16} strokeWidth={1.8} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-sm font-medium text-[#2C2A29] flex items-center gap-2 flex-wrap">
                    Use other chats in this workspace as context
                    <span
                      className={`px-2 py-0.5 rounded-md font-sans text-[10px] uppercase tracking-[0.12em] font-semibold ${
                        memoryOn
                          ? "bg-[#7A4A2C] text-white"
                          : "bg-[#EFEAE0] text-[#7A7571]"
                      }`}
                      data-testid="project-memory-state-pill"
                    >
                      {memoryOn ? "On" : "Off"}
                    </span>
                  </div>
                  <div className="font-sans text-xs text-[#7A7571] mt-1 leading-relaxed">
                    When on, Claude can refer to summaries of every chat in
                    this workspace — giving you continuity across
                    conversations. Private messages (<code>/private</code>) and
                    <code>@-mention</code> DMs are never included. Increases
                    token usage per message. Owner / Admin only.
                  </div>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={memoryOn}
                disabled={!isAdminOrOwner || memorySaving}
                onClick={handleMemoryToggle}
                data-testid="project-memory-toggle"
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-0 p-0 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 disabled:opacity-50 disabled:cursor-not-allowed ${
                  memoryOn ? "bg-[#7A4A2C]" : "bg-[#D6D2C9]"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    memoryOn ? "translate-x-[22px]" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
            {!isAdminOrOwner ? (
              <p
                className="font-sans text-xs text-[#A5A29E]"
                data-testid="project-memory-readonly-note"
              >
                Only Owner or Admin can change this. You're viewing as Member.
              </p>
            ) : null}
          </section>

          {/* Members */}
          <section
            className="space-y-3 mt-6"
            data-testid="settings-section-members"
          >
            <InvitesPanel
              workspace={workspace}
              currentUserRole={workspace?.role}
            />
            <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#7A7571] font-semibold mt-6">
              Members · {members.length}
            </h3>
            {loadingMembers ? (
              <p className="font-sans text-sm text-[#7A7571] flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Loading…
              </p>
            ) : (
              <ul
                className="divide-y divide-[#EFEAE0] border border-[#DCD7CE] rounded-xl overflow-hidden"
                data-testid="settings-members-list"
              >
                {members.map((m) => {
                  const isMe = m.user_id === user.id;
                  const targetIsOwner = m.role === "owner";
                  const canEditRole = isOwner && !isMe && !targetIsOwner;
                  const canRemoveOther =
                    !isMe &&
                    !targetIsOwner &&
                    isAdminOrOwner &&
                    !(m.role === "admin" && !isOwner);
                  const canLeave = isMe && !targetIsOwner;
                  return (
                    <li
                      key={m.user_id}
                      className="flex items-center gap-3 px-4 py-3 bg-white"
                      data-testid={`settings-member-row-${m.user_id}`}
                    >
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center font-serif text-white text-sm shrink-0 shadow-sm"
                        style={{ background: avatarBg(m.display_name) }}
                        aria-hidden="true"
                      >
                        {m.display_name?.[0] || "?"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-sans text-sm font-medium text-[#2C2A29] truncate">
                          {m.display_name}
                          {isMe ? (
                            <span className="text-[#7A7571] font-normal">
                              {" "}
                              · you
                            </span>
                          ) : null}
                        </div>
                        <div className="font-sans text-xs text-[#7A7571] truncate">
                          {m.email}
                        </div>
                      </div>
                      {canEditRole ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              data-testid={`settings-role-button-${m.user_id}`}
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-[#DCD7CE] font-sans text-xs text-[#2C2A29] hover:bg-[#F3F1EC] transition-colors"
                            >
                              {ROLE_LABEL[m.role]}
                              <ChevronDown size={12} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => changeRole(m, "admin")}
                              data-testid={`settings-role-set-admin-${m.user_id}`}
                            >
                              Admin
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => changeRole(m, "member")}
                              data-testid={`settings-role-set-member-${m.user_id}`}
                            >
                              Member
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setTransferTarget(m);
                                setTransferOpen(true);
                              }}
                              data-testid={`settings-role-make-owner-${m.user_id}`}
                            >
                              <Crown size={12} className="mr-2" /> Make
                              owner…
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span
                          className={`px-2.5 py-1 rounded-md font-sans text-xs flex items-center gap-1 ${
                            targetIsOwner
                              ? "bg-[#FAE7DC] text-[#7A4A2C] border border-[#D96C4A]/30"
                              : "bg-[#F3F1EC] text-[#7A7571] border border-[#DCD7CE]"
                          }`}
                          data-testid={`settings-role-badge-${m.user_id}`}
                        >
                          {targetIsOwner ? <Crown size={11} /> : null}
                          {ROLE_LABEL[m.role]}
                        </span>
                      )}
                      {canRemoveOther ? (
                        <button
                          type="button"
                          onClick={() => removeMember(m)}
                          className="p-1.5 rounded-md text-[#A5A29E] hover:text-[#8C3A1F] hover:bg-[#FAF2F0] transition-colors"
                          data-testid={`settings-remove-member-${m.user_id}`}
                          title={`Remove ${m.display_name}`}
                          aria-label={`Remove ${m.display_name}`}
                        >
                          <UserMinus size={14} />
                        </button>
                      ) : null}
                      {canLeave ? (
                        <button
                          type="button"
                          onClick={() => setLeaveOpen(true)}
                          className="p-1.5 rounded-md text-[#A5A29E] hover:text-[#8C3A1F] hover:bg-[#FAF2F0] transition-colors"
                          data-testid={`settings-leave-button-${m.user_id}`}
                          title="Leave workspace"
                          aria-label="Leave workspace"
                        >
                          <LogOut size={14} />
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {/* Danger zone — owner only */}
          {isOwner ? (
            <section
              className="space-y-3 mt-6"
              data-testid="settings-section-danger"
            >
              <h3 className="font-sans text-[10px] uppercase tracking-[0.22em] text-[#8C3A1F] font-semibold">
                Danger zone
              </h3>
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-[#8C3A1F]/20 bg-[#FAF2F0]">
                <div className="min-w-0">
                  <div className="font-sans text-sm font-medium text-[#2C2A29]">
                    Delete this workspace
                  </div>
                  <div className="font-sans text-xs text-[#7A7571] mt-0.5">
                    Removes the workspace, all chats, and all memberships. No
                    undo.
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="settings-delete-workspace-button"
                  className="bg-[#8C3A1F] hover:bg-[#7A3019] text-white shrink-0"
                >
                  <Trash2 size={14} className="mr-1.5" />
                  Delete
                </Button>
              </div>
            </section>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Leave confirmation */}
      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent data-testid="settings-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Leave {workspace.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You'll lose access to every chat here. The owner can re-invite
              you later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-leave-cancel">
              Stay
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={leaveWorkspace}
              data-testid="settings-leave-confirm"
              className="bg-[#8C3A1F] hover:bg-[#7A3019]"
            >
              Leave workspace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer ownership */}
      <AlertDialog open={transferOpen} onOpenChange={setTransferOpen}>
        <AlertDialogContent data-testid="settings-transfer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">
              Make {transferTarget?.display_name} the owner?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They'll get full control of{" "}
              <span className="font-medium text-[#2C2A29]">
                {workspace.name}
              </span>
              . You'll be demoted to admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-transfer-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                transferTarget && transferOwnership(transferTarget.user_id)
              }
              data-testid="settings-transfer-confirm"
              className="bg-[#2C2A29] hover:bg-[#1A1918]"
            >
              Transfer ownership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete workspace */}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(v) => {
          setDeleteOpen(v);
          if (!v) setDeleteConfirm("");
        }}
      >
        <AlertDialogContent data-testid="settings-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#8C3A1F]">
              Delete {workspace.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the workspace, every chat inside it, and
              every membership. Type{" "}
              <span className="font-medium text-[#2C2A29]">
                {workspace.name}
              </span>{" "}
              to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            data-testid="settings-delete-confirm-input"
            placeholder={workspace.name}
            className="w-full px-4 py-2.5 rounded-lg bg-white border border-[#8C3A1F]/30 font-sans text-base text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-[#8C3A1F]/30"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteConfirm !== workspace.name}
              onClick={deleteWorkspace}
              data-testid="settings-delete-confirm-button"
              className="bg-[#8C3A1F] hover:bg-[#7A3019] disabled:opacity-40"
            >
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 7b — Project memory enable confirmation */}
      <AlertDialog
        open={memoryConfirmOpen}
        onOpenChange={(v) => {
          setMemoryConfirmOpen(v);
          if (!v) setMemoryEstimate(null);
        }}
      >
        <AlertDialogContent data-testid="project-memory-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Brain size={20} className="text-[#7A4A2C]" /> Turn on project
              memory?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-sans text-sm text-[#7A7571] leading-relaxed">
              Claude will start seeing summaries of every chat in{" "}
              <span className="font-medium text-[#2C2A29]">
                {workspace.name}
              </span>{" "}
              when answering. Private messages and @-mention DMs are still
              hidden. You can turn this off any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div
            className="rounded-xl border border-[#DCD7CE] bg-[#FBF8F2] px-4 py-3 my-2"
            data-testid="project-memory-cost-card"
          >
            {memoryEstimate === null ? (
              <p className="font-sans text-sm text-[#7A7571] flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} />
                Calculating estimate…
              </p>
            ) : (
              <ul className="space-y-1.5 font-sans text-sm text-[#2C2A29]">
                <li className="flex items-baseline justify-between gap-3">
                  <span className="text-[#7A7571]">Chats to include</span>
                  <span
                    className="font-medium"
                    data-testid="project-memory-est-chats"
                  >
                    {memoryEstimate.summarizable_chats}
                  </span>
                </li>
                <li className="flex items-baseline justify-between gap-3">
                  <span className="text-[#7A7571]">
                    Added context per Claude reply
                  </span>
                  <span
                    className="font-medium"
                    data-testid="project-memory-est-tokens"
                  >
                    ~
                    {memoryEstimate.estimated_added_input_tokens_per_call.toLocaleString()}{" "}
                    tokens
                  </span>
                </li>
                <li className="flex items-baseline justify-between gap-3">
                  <span className="text-[#7A7571]">
                    Extra cost per message (Opus)
                  </span>
                  <span
                    className="font-medium"
                    data-testid="project-memory-est-cents"
                  >
                    ~
                    {memoryEstimate.estimated_added_cost_per_call_cents.toFixed(
                      3
                    )}
                    ¢
                  </span>
                </li>
                <li className="flex items-baseline justify-between gap-3 text-[11px] text-[#A5A29E] pt-1 border-t border-[#EFEAE0] mt-2">
                  <span>Token cap</span>
                  <span>{memoryEstimate.cap_tokens.toLocaleString()}</span>
                </li>
              </ul>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="project-memory-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setMemoryConfirmOpen(false);
                setMemoryEstimate(null);
                setProjectMemory(true);
              }}
              data-testid="project-memory-confirm-enable"
              className="bg-[#7A4A2C] hover:bg-[#5E371E]"
            >
              Enable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
